/**
 * One person's month, day by day, behind the counts on their row.
 *
 * The month table answers "how many" and could not answer "which" — a row
 * reading four absences had no way to say which four. Worse since the
 * Unmonitored column was dropped: the visible counts stopped adding up to
 * Scheduled, so a row that would not reconcile had nothing behind it to
 * explain the gap. This is that explanation, and the summary strip at the top
 * is the same numbers as the row it was opened from — carried from the server
 * in one response, not recomputed here from the days below.
 *
 * A panel rather than a page. The month stays visible behind it, which is what
 * makes "this row, why?" a glance rather than a journey, and it matches
 * AttendanceDetailPanel and EmployeeDetailPanel — the two other things that
 * open over a table on these screens.
 *
 * Every calendar day the month examined is listed, including the ones nobody
 * worked. A weekly off is greyed rather than omitted, on the day view's own
 * tone rule: grey is "the day was never theirs", amber is "somebody should
 * look". Omitting them would make the list read as a work log and quietly
 * break the arithmetic the strip above it promises.
 */
import { useQuery } from '@tanstack/react-query';
import { Loader2, X } from 'lucide-react';
import { client } from '../api/client';
import { titleCaseName } from '../lib/titleCase';
import { useDismissOnEscape } from '../lib/useDismiss';
import { isWithdrawn, WITHDRAWN_HINT, WITHDRAWN_LABEL } from '../lib/withdrawn';
import { ATTENDANCE_STATUS_LABELS } from '../lib/attendanceLabels';
import type {
  AttendanceMonthly,
  AttendanceMonthlyDay,
  AttendanceMonthlyDetail,
  AttendanceRecordStatus,
} from '../types';

/**
 * The same labels and the same three tones the day view uses.
 *
 * Copied in shape rather than imported, because that map lives inside a page
 * module. If a third screen ever needs it the two should be lifted into
 * lib/ together; two is not yet a pattern, and a shared constant that exists
 * for one caller is its own kind of indirection.
 */
const STATUS: Record<AttendanceRecordStatus, { label: string; tone: string }> = {
  present: { label: ATTENDANCE_STATUS_LABELS.present, tone: 'bg-emerald-100 text-emerald-700' },
  late: { label: ATTENDANCE_STATUS_LABELS.late, tone: 'bg-amber-100 text-amber-800' },
  half_day: { label: ATTENDANCE_STATUS_LABELS.half_day, tone: 'bg-amber-100 text-amber-700' },
  unmonitored: { label: ATTENDANCE_STATUS_LABELS.unmonitored, tone: 'bg-gray-100 text-gray-600' },
  absent: { label: ATTENDANCE_STATUS_LABELS.absent, tone: 'bg-red-100 text-red-700' },
  not_yet_started: { label: ATTENDANCE_STATUS_LABELS.not_yet_started, tone: 'bg-amber-100 text-amber-800' },
  not_due_yet: { label: ATTENDANCE_STATUS_LABELS.not_due_yet, tone: 'bg-gray-100 text-gray-500' },
  leave: { label: ATTENDANCE_STATUS_LABELS.leave, tone: 'bg-sky-100 text-sky-700' },
  holiday: { label: ATTENDANCE_STATUS_LABELS.holiday, tone: 'bg-sky-100 text-sky-700' },
  weekly_off: { label: ATTENDANCE_STATUS_LABELS.weekly_off, tone: 'bg-gray-100 text-gray-500' },
  no_shift_assigned: { label: ATTENDANCE_STATUS_LABELS.no_shift_assigned, tone: 'bg-gray-100 text-gray-600' },
};

/** The four the strip shows, in the order the table shows them. */
/**
 * The four the strip shows, in the order the table shows them.
 *
 * `hint` exists for Late alone, and for a real question: the tile can read 0
 * while rows below it show a late start. `_resolve` says why -- "LATE
 * displaces PRESENT only. A half day or an unmonitored day says something
 * about the whole day that late would paper over, and the late_by_minutes
 * column carries the figure either way." The count is right and the rows are
 * right; what was missing was anything saying so.
 */
const SUMMARY_KEYS = [
  { key: 'present', label: 'Present', tone: 'text-emerald-700', hint: undefined },
  {
    key: 'late',
    label: 'Late',
    tone: 'text-amber-700',
    hint:
      'Counts days whose only fault was a late start. A late start on a half '
      + 'day or an unmonitored day is shown on the row but counted under that '
      + 'status instead.',
  },
  { key: 'halfDay', label: 'Half day', tone: 'text-amber-700', hint: undefined },
  { key: 'absent', label: 'Absent', tone: 'text-red-700', hint: undefined },
] as const;

const WEEKDAYS = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];

function duration(minutes: number | null | undefined): string | null {
  if (!minutes) return null;
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}

/** "Mon 01" — the weekday matters more than the year on a month of one. */
function dayLabel(iso: string): { weekday: string; day: string } {
  const [, , d] = iso.split('-');
  // Parsed as a local date rather than through Date(iso), which reads a bare
  // yyyy-mm-dd as UTC and lands on the previous evening east of Greenwich —
  // every row would name the wrong weekday in IST.
  const [y, mo, dd] = iso.split('-').map(Number);
  const weekday = WEEKDAYS[(new Date(y, mo - 1, dd).getDay() + 6) % 7];
  return { weekday, day: d };
}

export default function AttendanceMonthPanel({
  userId,
  userName,
  year,
  month,
  onClose,
  onOpenDay,
}: {
  userId: string;
  /** Shown while the request is in flight, so the panel names itself at once. */
  userName: string | null;
  year: number;
  month: number;
  onClose: () => void;
  /** A day was chosen. The caller opens the existing day detail for it. */
  onOpenDay: (day: AttendanceMonthlyDay) => void;
}) {
  // Closes this panel and, when the day detail is open over it, only that
  // one -- see useDismissOnEscape for how the two are told apart.
  useDismissOnEscape(onClose);

  const detail = useQuery<AttendanceMonthlyDetail>({
    queryKey: ['attendance', 'month-detail', userId, year, month],
    queryFn: async () =>
      (
        await client.get(`/api/v1/attendance/records/monthly/${userId}`, {
          params: { year, month },
        })
      ).data,
  });

  const summary: AttendanceMonthly | undefined = detail.data?.summary;
  const days = detail.data?.days ?? [];
  const withdrawnDays = days.filter(isWithdrawn).length;

  return (
    // Backdrop closes, panel does not -- the pairing every other drawer in
    // the app uses. Without the stopPropagation below, a click anywhere
    // inside would bubble to the backdrop and shut the thing being read.
    <div
      className="fixed inset-0 z-30 flex justify-end bg-black/30"
      onClick={onClose}
    >
      <aside
        className="flex h-full w-full max-w-2xl flex-col bg-white shadow-xl"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="flex items-start justify-between border-b border-gray-200 px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-gray-900">
              {titleCaseName(userName) ?? 'This person'}
            </h2>
            <p className="mt-0.5 text-xs text-gray-500">
              {new Date(year, month - 1, 1).toLocaleString('en-IN', {
                month: 'long',
                year: 'numeric',
              })}
              {summary?.designation ? ` · ${summary.designation}` : ''}
            </p>
          </div>
          <button
            type="button"
            className="btn-ghost"
            onClick={onClose}
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </header>

        {detail.isLoading ? (
          <div className="flex flex-1 items-center justify-center text-gray-400">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : detail.isError ? (
          <div className="m-5 rounded-lg bg-red-50 px-4 py-3 text-sm text-red-800">
            Could not load this month.
          </div>
        ) : (
          <>
            {/* The row's own numbers, not a tally of what is below.
                They come down in the same response the days did, from the
                same pass of the same loop — so the strip and the table row
                behind this panel cannot disagree. */}
            {summary && (
              <div className="grid grid-cols-4 gap-px border-b border-gray-200 bg-gray-200">
                {SUMMARY_KEYS.map(({ key, label, tone, hint }) => (
                  <div key={key} className="bg-white px-5 py-3" title={hint}>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">
                      {label}
                      {hint && (
                        <span
                          className="ml-1 cursor-help text-gray-400"
                          aria-hidden
                        >
                          ?
                        </span>
                      )}
                    </p>
                    <p className={`text-xl font-semibold tabular-nums ${tone}`}>
                      {summary[key]}
                    </p>
                  </div>
                ))}
              </div>
            )}

            {summary && (
              <p className="border-b border-gray-100 px-5 py-2 text-[11px] text-gray-400">
                {summary.scheduledDays} scheduled of {summary.daysCounted} days
                examined. A month still running stops at today.
              </p>
            )}

            {/* `sticky-head` rather than a sticky <thead> of its own.
                Tailwind's preflight collapses borders, so a bottom border on
                the row belongs to the table and not to the sticky layer: it
                painted as a fragment under one column and the rows travelled
                visibly under a header that was not opaque. The shared rule
                puts the background and an inset rule on the cells, which is
                the layer that actually sticks. index.css says all of this
                where the rule lives; this comment exists because the class
                name alone does not explain why the obvious markup is wrong. */}
            <div className="sticky-head flex-1 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="text-left text-xs text-gray-500">
                  <tr>
                    <th className="px-5 py-2 font-medium">Day</th>
                    <th className="px-3 py-2 font-medium">Status</th>
                    <th className="px-3 py-2 text-right font-medium">Late By</th>
                    <th className="px-3 py-2 text-right font-medium">Worked</th>
                    <th className="px-3 py-2 text-right font-medium">Check-ins</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {days.map((day) => {
                    const meta = STATUS[day.status];
                    const { weekday, day: dd } = dayLabel(day.workDate);
                    // Grey for a day that was never theirs, on the day view's
                    // own rule. It reads as context rather than as a finding.
                    const muted = !day.scheduled;
                    return (
                      <tr
                        key={day.workDate}
                        className={`cursor-pointer hover:bg-gray-50 ${muted ? 'text-gray-400' : ''}`}
                        onClick={() => onOpenDay(day)}
                        title="Open this day's check-ins"
                      >
                        <td className="whitespace-nowrap px-5 py-2">
                          <span className="tabular-nums font-medium text-gray-900">
                            {dd}
                          </span>
                          <span className="ml-1.5 text-xs text-gray-500">
                            {weekday}
                          </span>
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span className={`badge ${meta.tone}`}>{meta.label}</span>
                          {day.inProgress && (
                            <span className="ml-1 text-xs text-gray-400">
                              in progress
                            </span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                          {/* The same duration() the Worked column uses.
                              A bare `238m` is the stored unit, not a length
                              anybody reads -- and two time columns side by
                              side in different formats is worse than either
                              format alone. */}
                          {duration(day.lateByMinutes) ?? (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right tabular-nums">
                          {duration(day.workedMinutes) ?? (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 text-right">
                          {/* A withdrawn day shows the withdrawal instead of
                              a figure, like everywhere else: a percentage
                              printed beside a withdrawal has withdrawn
                              nothing. */}
                          {isWithdrawn(day) ? (
                            <span
                              className="text-xs text-amber-700"
                              title={day.scoringWithdrawnReason ?? WITHDRAWN_HINT}
                            >
                              {WITHDRAWN_LABEL}
                            </span>
                          ) : day.availabilityPct != null ? (
                            <>
                              <span className="tabular-nums">
                                {`${Number(day.availabilityPct).toFixed(0)}%`}
                              </span>
                              <span className="ml-1.5 text-xs text-gray-500">
                                {day.respondedPings}/{day.expectedPings}
                              </span>
                            </>
                          ) : (
                            <span className="text-gray-300">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                  {days.length === 0 && (
                    <tr>
                      <td
                        colSpan={5}
                        className="px-5 py-8 text-center text-sm text-gray-400"
                      >
                        Nothing examined for this month.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Said once under the table rather than only in a tooltip on each
                row. A withdrawn day is not a gap in the data somebody should
                go looking for -- it is a decision that was taken, and the
                reason is not guessable from the words "Not scored".

                Only when there is one. A standing note about a state no day
                on screen is in is noise on every other month. */}
            {withdrawnDays > 0 && (
              <p className="border-t border-gray-100 px-5 py-2.5 text-[11px] leading-relaxed text-gray-500">
                <span className="font-medium text-amber-700">Not scored</span>
                {' '}on {withdrawnDays === 1 ? 'one day' : `${withdrawnDays} days`}:
                check-ins were not reaching this person&rsquo;s browser, so
                those prompts were discounted and the day&rsquo;s score
                withdrawn rather than recalculated. Attendance is unaffected —
                the day still counts as worked.
              </p>
            )}
          </>
        )}
      </aside>
    </div>
  );
}
